import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { CheckCircle2, Loader2, Shield, CreditCard, ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PLANS = {
  starter: { id: 'starter', allInclusive: 199, setupMonthly: { setup: 299, monthly: 149 } },
  professional: { id: 'professional', allInclusive: 249, setupMonthly: { setup: 399, monthly: 199 } },
  agency: { id: 'agency', allInclusive: 399, setupMonthly: { setup: 499, monthly: 299 } },
};

const CC_FEE = 3.55;

export default function SignupPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['signup', 'home']);
  const planId = searchParams.get('plan') || 'professional';
  const referralCode = searchParams.get('ref') || (document.cookie.match(/wts_ref=([^;]+)/)?.[1] ? decodeURIComponent(document.cookie.match(/wts_ref=([^;]+)/)[1]) : '');
  const plan = PLANS[planId] || PLANS.professional;
  const planName = t(`home:pricing.${plan.id}.name`);
  const planDesc = t(`home:pricing.${plan.id}.desc`);
  const planFeatures = t(`home:pricing.${plan.id}.features`, { returnObjects: true }) || [];

  const [billingModel, setBillingModel] = useState('all_inclusive');
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [email, setEmail] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [trialMode, setTrialMode] = useState(false);
  const [trialSuccess, setTrialSuccess] = useState(null);

  const monthly = billingModel === 'all_inclusive' ? plan.allInclusive : plan.setupMonthly.monthly;
  const setupFee = billingModel === 'setup_monthly' ? plan.setupMonthly.setup : 0;
  const monthlyFee = Math.round(monthly * CC_FEE) / 100;
  const setupFeeFee = setupFee ? Math.round(setupFee * CC_FEE) / 100 : 0;
  const totalFirst = monthly + monthlyFee + setupFee + setupFeeFee;
  const totalMonthly = monthly + monthlyFee;
  const lang = (i18n.language || 'en').split('-')[0];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !businessName) return;
    setSubmitting(true);
    setError('');

    if (trialMode) {
      // "$1 for 90 days" intro offer — card collected via Stripe, $1 charged today,
      // recurring plan auto-bills the regular monthly price after 90 days.
      try {
        const res = await fetch(`${API}/api/signup/checkout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email, business_name: businessName, phone,
            plan: planId, billing_model: 'all_inclusive',
            intro_offer: true,
            origin_url: window.location.origin,
            referral_code: referralCode,
            preferred_language: lang,
          }),
        });
        const data = await res.json();
        if (data.url) {
          window.location.href = data.url;
          return;
        }
        setError(data.detail || t('signup:errorCheckout'));
      } catch (err) {
        setError(t('signup:errorGeneric'));
      }
      setSubmitting(false);
      return;
    }
    try {
      const endpoint = paymentMethod === 'paypal'
        ? `${API}/api/signup/checkout-paypal`
        : `${API}/api/signup/checkout`;

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email, business_name: businessName, phone,
          plan: planId, billing_model: billingModel,
          origin_url: window.location.origin,
          referral_code: referralCode,
          preferred_language: lang,
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError(data.detail || t('signup:errorCheckout'));
      }
    } catch (err) {
      setError(t('signup:errorGeneric'));
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-slate-500 hover:text-slate-800 text-sm" data-testid="signup-back-btn">
            <ArrowLeft className="h-4 w-4" /> {t('signup:back')}
          </button>
          <p className="text-sm font-bold" style={{ color: '#002FA7', fontFamily: "'Outfit', sans-serif" }}>LocalBranding Pro</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-10 sm:py-16">
        <div className="text-center mb-10">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="signup-heading">
            {trialMode ? t('signup:trialHeading') : t('signup:planHeading', { plan: planName })}
          </h1>
          <p className="text-slate-500 mt-2 text-sm">
            {trialMode ? t('signup:trialSub') : planDesc}
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3">
            {trialSuccess ? (
              <Card className="border-green-200 bg-green-50 shadow-sm">
                <CardContent className="p-6 sm:p-8 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <h2 className="text-xl font-bold text-green-800 mb-2">{t('signup:trialSuccessTitle')}</h2>
                  <p className="text-green-700 text-sm mb-6" dangerouslySetInnerHTML={{ __html: t('signup:trialSuccessSub', { email }) }} />
                  <div className="bg-white rounded-lg p-4 text-left mb-4 border border-green-200">
                    <p className="text-sm text-slate-700 mb-2"><strong>{t('signup:whatHappensNext')}</strong></p>
                    <ol className="text-sm text-slate-600 space-y-2 list-decimal list-inside">
                      {(t('signup:trialNextSteps', { returnObjects: true }) || []).map((step, i) => (
                        <li key={i}>{step}</li>
                      ))}
                    </ol>
                  </div>
                  <div className="flex gap-3 justify-center">
                    <Button onClick={() => navigate('/client/login')} className="bg-green-600 hover:bg-green-700 text-white" data-testid="trial-go-to-login">
                      {t('signup:goToDashboard')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-6 sm:p-8">
                <div className={`mb-6 p-4 rounded-xl border-2 cursor-pointer transition-all ${trialMode ? 'border-green-500 bg-green-50' : 'border-dashed border-slate-300 hover:border-green-400 hover:bg-green-50/50'}`}
                  onClick={() => setTrialMode(!trialMode)} data-testid="trial-toggle">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-slate-900 text-sm flex items-center gap-2">
                        {trialMode && <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>}
                        {t('signup:trialBannerTitle')}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">{t('signup:trialBannerSub')}</p>
                    </div>
                    <div className={`w-10 h-6 rounded-full transition-colors ${trialMode ? 'bg-green-500' : 'bg-slate-300'}`}>
                      <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform mt-0.5 ${trialMode ? 'translate-x-4.5 ml-[18px]' : 'ml-0.5'}`} />
                    </div>
                  </div>
                </div>

                <h2 className="text-lg font-bold text-slate-900 mb-6">{trialMode ? t('signup:startTrial') : t('signup:yourInfo')}</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label className="text-sm text-slate-700">{t('signup:businessName')}</Label>
                    <Input
                      value={businessName} onChange={(e) => setBusinessName(e.target.value)}
                      placeholder={t('signup:businessNamePlaceholder')} required
                      className="mt-1" data-testid="signup-business-name"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-700">{t('signup:email')}</Label>
                    <Input
                      type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('signup:emailPlaceholder')} required
                      className="mt-1" data-testid="signup-email"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-700">{t('signup:phone')}</Label>
                    <Input
                      type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                      placeholder={t('signup:phonePlaceholder')}
                      className="mt-1" data-testid="signup-phone"
                    />
                  </div>

                  {!trialMode && (
                  <div className="pt-2">
                    <Label className="text-sm text-slate-700 mb-2 block">{t('signup:billingOption')}</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <button type="button"
                        onClick={() => setBillingModel('all_inclusive')}
                        className={`p-3 rounded-lg border text-left text-sm transition-all ${billingModel === 'all_inclusive' ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-slate-200 hover:border-slate-300'}`}
                        data-testid="billing-all-inclusive"
                      >
                        <p className="font-semibold text-slate-900">${plan.allInclusive}{t('signup:perMo')}</p>
                        <p className="text-[10px] text-slate-500 mt-0.5">{t('signup:allInclusive')}</p>
                      </button>
                      <button type="button"
                        onClick={() => setBillingModel('setup_monthly')}
                        className={`p-3 rounded-lg border text-left text-sm transition-all ${billingModel === 'setup_monthly' ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-slate-200 hover:border-slate-300'}`}
                        data-testid="billing-setup-monthly"
                      >
                        <p className="font-semibold text-slate-900">${plan.setupMonthly.monthly}{t('signup:perMo')}</p>
                        <p className="text-[10px] text-slate-500 mt-0.5">{t('signup:setupMonthly', { setup: plan.setupMonthly.setup })}</p>
                      </button>
                    </div>
                  </div>
                  )}

                  {!trialMode && (
                  <div className="pt-2">
                    <Label className="text-sm text-slate-700 mb-2 block">{t('signup:paymentMethod')}</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <button type="button"
                        onClick={() => setPaymentMethod('stripe')}
                        className={`p-3 rounded-lg border text-left text-sm transition-all flex items-center gap-2 ${paymentMethod === 'stripe' ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-slate-200 hover:border-slate-300'}`}
                        data-testid="payment-stripe"
                      >
                        <CreditCard className="h-4 w-4 text-slate-600 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-slate-900">{t('signup:card')}</p>
                          <p className="text-[10px] text-slate-500 mt-0.5">{t('signup:cardSub')}</p>
                        </div>
                      </button>
                      <button type="button"
                        onClick={() => setPaymentMethod('paypal')}
                        className={`p-3 rounded-lg border text-left text-sm transition-all flex items-center gap-2 ${paymentMethod === 'paypal' ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-slate-200 hover:border-slate-300'}`}
                        data-testid="payment-paypal"
                      >
                        <svg className="h-4 w-4 flex-shrink-0" viewBox="0 0 24 24" fill="none"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106z" fill="#253B80"/><path d="M20.16 7.434c-.012.082-.025.163-.04.248-1.177 6.047-5.207 8.13-10.354 8.13H7.56a1.273 1.273 0 0 0-1.258 1.076l-1.33 8.436-.377 2.39a.669.669 0 0 0 .66.773h4.632c.55 0 1.017-.4 1.103-.943l.045-.234.873-5.533.056-.306c.086-.543.552-.943 1.103-.943h.694c4.496 0 8.014-1.826 9.044-7.107.43-2.208.208-4.05-1.044-5.444a4.16 4.16 0 0 0-1.427-1.043z" fill="#179BD7"/></svg>
                        <div>
                          <p className="font-semibold text-slate-900">{t('signup:paypal')}</p>
                          <p className="text-[10px] text-slate-500 mt-0.5">{t('signup:paypalSub')}</p>
                        </div>
                      </button>
                    </div>
                  </div>
                  )}

                  {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded" data-testid="signup-error">{error}</p>}

                  <Button
                    type="submit" disabled={submitting || !email || !businessName}
                    className="w-full h-11 text-sm mt-2"
                    style={{ background: trialMode ? '#16a34a' : paymentMethod === 'paypal' ? '#0070BA' : '#002FA7' }}
                    data-testid="signup-submit-btn"
                  >
                    {submitting ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('signup:processing')}</>
                    ) : trialMode ? (
                      <>{t('signup:btnTrial')}</>
                    ) : paymentMethod === 'paypal' ? (
                      <>{t('signup:btnPaypal', { total: totalFirst.toFixed(2) })}</>
                    ) : (
                      <><CreditCard className="h-4 w-4 mr-2" /> {t('signup:btnCard', { total: totalFirst.toFixed(2) })}</>
                    )}
                  </Button>

                  <div className="flex items-center justify-center gap-2 text-[10px] text-slate-400 pt-1">
                    <Shield className="h-3 w-3" />
                    <span>{trialMode ? t('signup:secureTrial') : t('signup:secureCheckout')}</span>
                  </div>
                </form>
              </CardContent>
            </Card>
            )}
          </div>
          <div className="lg:col-span-2">
            <Card className="border-slate-200 shadow-sm sticky top-8">
              <CardContent className="p-6">
                {trialMode ? (
                  <>
                    <h3 className="font-bold text-slate-900 mb-4">{t('signup:trialDetails')}</h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">{t('signup:planMonthly', { plan: planName })}</span>
                        <Badge className="bg-green-100 text-green-800 text-xs font-semibold">{t('signup:sevenDaysFree')}</Badge>
                      </div>
                      <div className="border-t pt-3">
                        <div className="flex justify-between font-bold text-green-700 text-lg">
                          <span>{t('signup:dueToday')}</span>
                          <span>$1.00</span>
                        </div>
                        <div className="flex justify-between text-xs text-slate-400 mt-1">
                          <span>{t('signup:thenMonthly90')}</span>
                          <span>${totalMonthly.toFixed(2)}{t('signup:perMo')}</span>
                        </div>
                        <p className="text-xs text-slate-400 mt-2">{t('signup:noCardNote')}</p>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-2">
                        <p className="text-xs font-semibold text-green-800 mb-1">{t('signup:whatYouGet')}</p>
                        <ul className="text-xs text-green-700 space-y-1">
                          {(t('signup:trialPerks', { returnObjects: true, plan: planName }) || []).map((p, i) => (
                            <li key={i}>{p}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <h3 className="font-bold text-slate-900 mb-4">{t('signup:orderSummary')}</h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">{t('signup:planMonthlySummary', { plan: planName })}</span>
                        <span className="font-semibold">${monthly.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-slate-400 text-xs">
                        <span>{t('signup:processingFee', { pct: CC_FEE })}</span>
                        <span>${monthlyFee.toFixed(2)}</span>
                      </div>
                      {billingModel === 'setup_monthly' && (
                        <>
                          <div className="flex justify-between border-t pt-2">
                            <span className="text-slate-600">{t('signup:setupOneTime')}</span>
                            <span className="font-semibold">${setupFee.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-slate-400 text-xs">
                            <span>{t('signup:setupProcessingFee', { pct: CC_FEE })}</span>
                            <span>${setupFeeFee.toFixed(2)}</span>
                          </div>
                        </>
                      )}
                      <div className="border-t pt-3">
                        <div className="flex justify-between font-bold text-slate-900">
                          <span>{t('signup:dueToday')}</span>
                          <span>${totalFirst.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-xs text-slate-400 mt-1">
                          <span>{t('signup:thenMonthly')}</span>
                          <span>${totalMonthly.toFixed(2)}{t('signup:perMo')}</span>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                <div className="mt-6 pt-4 border-t">
                  <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-3">{t('signup:whatsIncluded')}</h4>
                  <ul className="space-y-2">
                    {planFeatures.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
